import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';
import type {
  CaseTypeCourtMappingCreateRequest,
  CaseTypeCourtMappingCreateResponse,
  CaseTypeCourtMappingGetAllResponse,
  CaseTypeCourtMappingMessageResponse,
  CaseTypeOption,
  CourtOption,
  CourtTypesResponse,
} from '@/types/caseTypeCourtMapping.types';
import type { GenericErrorResponse } from '@/types/api.types';

type ApiResult<T> = { data?: T; error?: GenericErrorResponse };

export const CASE_TYPE_COURT_MAPPINGS_QUERY_KEY = 'caseTypeCourtMappings';
export const PS_COURT_OPTIONS_QUERY_KEY = 'psCourtOptions';

export const getCaseTypeCourtMappings = async (
  caseTypeId: string
): Promise<ApiResult<CaseTypeCourtMappingGetAllResponse>> => {
  try {
    const response = await axiosInstance.get<CaseTypeCourtMappingGetAllResponse>('/case-type-court-mappings', {
      params: { caseTypeId },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const createCaseTypeCourtMapping = async (
  body: CaseTypeCourtMappingCreateRequest
): Promise<ApiResult<CaseTypeCourtMappingCreateResponse>> => {
  try {
    const response = await axiosInstance.post<CaseTypeCourtMappingCreateResponse>('/case-type-court-mappings', body);
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const deleteCaseTypeCourtMapping = async (
  mappingId: string
): Promise<ApiResult<CaseTypeCourtMappingMessageResponse>> => {
  try {
    const response = await axiosInstance.delete<CaseTypeCourtMappingMessageResponse>(
      `/case-type-court-mappings/${mappingId}`
    );
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const getCourtTypes = async (): Promise<ApiResult<CourtTypesResponse>> => {
  try {
    const response = await axiosInstance.get<CourtTypesResponse>('/courts/types');
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const getCaseTypes = async (): Promise<ApiResult<{ caseTypes: CaseTypeOption[] }>> => {
  try {
    const response = await axiosInstance.get<{ caseTypes: CaseTypeOption[] }>('/cases/types', {
      params: { sortField: 'name', sortDirection: 'asc' },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const searchCourts = async (search?: string): Promise<ApiResult<{ courts: CourtOption[] }>> => {
  try {
    const response = await axiosInstance.get<{ courts: CourtOption[] }>('/courts', {
      params: { search: search ? search : undefined, pageSize: 100, sortField: 'name', sortDirection: 'asc' },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

// With a case type, the API lists that case type's mapped courts first, then the rest, each A–Z.
// Without one, ask for plain name order so the list is still alphabetical.
export const searchCourtsForCaseType = async (
  params: { search?: string; pageSize?: number },
  caseTypeId?: string
): Promise<ApiResult<{ courts: CourtOption[] }>> => {
  try {
    const response = await axiosInstance.get<{ courts: CourtOption[] }>('/courts', {
      params: {
        search: params.search ? params.search : undefined,
        pageSize: params.pageSize ?? 100,
        ...(caseTypeId ? { caseTypeId } : { sortField: 'name', sortDirection: 'asc' }),
      },
    });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

const unwrap = async <T>(request: Promise<ApiResult<T>>): Promise<T> => {
  const { data, error } = await request;
  if (error) throw error;
  return data!;
};

export const useCaseTypeCourtMappings = (caseTypeId: string | undefined) =>
  useQuery<CaseTypeCourtMappingGetAllResponse, GenericErrorResponse>({
    queryKey: [CASE_TYPE_COURT_MAPPINGS_QUERY_KEY, caseTypeId],
    queryFn: () => unwrap(getCaseTypeCourtMappings(caseTypeId!)),
    enabled: !!caseTypeId,
  });

export const useCourtTypes = () =>
  useQuery<CourtTypesResponse, GenericErrorResponse>({
    queryKey: ['courtTypes'],
    queryFn: () => unwrap(getCourtTypes()),
    staleTime: 1000 * 60 * 5,
  });

export const useCaseTypes = () =>
  useQuery<{ caseTypes: CaseTypeOption[] }, GenericErrorResponse>({
    queryKey: ['caseTypeOptions'],
    queryFn: () => unwrap(getCaseTypes()),
    staleTime: 1000 * 60 * 5,
  });

// Shaped for FormFieldSearch's `api` prop; each option carries the court so the caller can read its type.
export const useCourtSearchOptions = (params: { search?: string }) => {
  const query = useQuery<{ courts: CourtOption[] }, GenericErrorResponse>({
    queryKey: ['courtSearchOptions', params.search],
    queryFn: () => unwrap(searchCourts(params.search)),
    staleTime: 1000 * 60 * 5,
  });

  const values = (query.data?.courts ?? []).map((court) => ({
    label: court.name,
    value: court.courtId,
    secondaryLabel: court.type,
    option: court,
  }));
  return { ...query, values };
};

// Process Service Court Name options (ZOHO-1828). Shaped for FormFieldSearch's `api` prop, which calls it
// during render, so it is named like the existing `courtSearch` rather than as a `use` hook.
// Labels match `courtSearch` (`CODE - Name`) so the field looks the same.
export const courtSearchForCaseType = (
  params: { search?: string; pageSize?: number },
  refreshKey: number | undefined,
  caseTypeId: string | undefined
) => {
  const query = useQuery<{ courts: CourtOption[] }, GenericErrorResponse>({
    queryKey: [PS_COURT_OPTIONS_QUERY_KEY, caseTypeId, params.search, params.pageSize, refreshKey],
    queryFn: () => unwrap(searchCourtsForCaseType(params, caseTypeId)),
    staleTime: 1000 * 60 * 5,
  });

  const values = (query.data?.courts ?? []).map((court) => ({
    label: `${court.code} - ${court.name}`,
    value: court.courtId,
  }));
  return { ...query, values };
};

// A mapping change also changes the Process Service court order, so drop those cached options too.
const invalidateMappingQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [CASE_TYPE_COURT_MAPPINGS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [PS_COURT_OPTIONS_QUERY_KEY] }),
  ]);

export const useCreateCaseTypeCourtMapping = () => {
  const queryClient = useQueryClient();
  return useMutation<CaseTypeCourtMappingCreateResponse, GenericErrorResponse, CaseTypeCourtMappingCreateRequest>({
    mutationFn: (body) => unwrap(createCaseTypeCourtMapping(body)),
    onSuccess: () => invalidateMappingQueries(queryClient),
  });
};

export const useDeleteCaseTypeCourtMapping = () => {
  const queryClient = useQueryClient();
  return useMutation<CaseTypeCourtMappingMessageResponse, GenericErrorResponse, string>({
    mutationFn: (mappingId) => unwrap(deleteCaseTypeCourtMapping(mappingId)),
    onSuccess: () => invalidateMappingQueries(queryClient),
  });
};
